import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { DEV_SCENARIO_COOKIE_NAME, devScenarioNames } from '@arya/shared/domain';
import { PLATFORM_BASE_PATH } from '@arya/shared/feature';
import { CookieStore } from '@arya/shared/infrastructure/cookie';

import { NoxoButton } from '@noxo/ui/button';
import { NoxoCardOverline } from '@noxo/ui/card';
import { NoxoRadio } from '@noxo/ui/radio';

import { DEV_SCENARIOS } from './dev-scenarios';
import { scenarioConsole } from './scenario-console';
import { pickedPerGroup, scenarioGroups } from './scenario-groups';

const pickedKey = (picked: Readonly<Record<string, string>>): string => Object.values(picked).toSorted().join(',');

@Component({
  selector: 'noxo-demo-scenarios',
  imports: [NoxoButton, NoxoCardOverline, NoxoRadio],
  templateUrl: './demo-scenarios.html',
  styleUrl: './demo-scenarios.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoScenarios {
  private readonly console = scenarioConsole(inject(DOCUMENT), inject(DEV_SCENARIOS));
  private readonly active = devScenarioNames(
    `${DEV_SCENARIO_COOKIE_NAME}=${inject(CookieStore).get(DEV_SCENARIO_COOKIE_NAME) ?? ''}`
  );
  private readonly initial = pickedPerGroup(this.console.list(), this.active);
  private readonly picked = signal(this.initial);

  protected readonly basePath = inject(PLATFORM_BASE_PATH);
  protected readonly groups = scenarioGroups(this.console.list());
  protected readonly hasActive = this.active.length > 0;
  protected readonly selection = this.picked.asReadonly();
  protected readonly changed = computed(() => pickedKey(this.picked()) !== pickedKey(this.initial));

  protected pick(group: string, name?: string): void {
    this.picked.update((picked) => {
      const others = Object.fromEntries(Object.entries(picked).filter(([key]) => key !== group));
      return name === undefined ? others : { ...others, [group]: name };
    });
  }

  protected apply(): void {
    const names = Object.values(this.picked());
    if (names.length === 0) {
      this.console.clear();
      return;
    }
    this.console.use(...names);
  }

  protected clear(): void {
    this.console.clear();
  }
}
